import React, { useEffect, useState } from 'react';
import type { ModerationProfileBase } from '../types/moderationProfile';
import { PROFILE_DETAIL_FIELDS } from '../types/moderationProfile';
import {
  formatProfileFieldDisplay,
  getProfileFieldValue,
  getProfileListLabel,
} from '../utils/moderationProfile';

interface ModerationReviewPanelProps {
  profiles: ModerationProfileBase[];
  emptyMessage: string;
  selectedProfileId: string | null;
  onSelectProfile: (profileId: string) => void;
  onApprove: (profileId: string) => void;
  onReject: (profileId: string) => void;
  onMoveToPending: (profileId: string) => void;
  onDelete: (profileId: string) => void;
  footer?: React.ReactNode;
}

const ModerationReviewPanel: React.FC<ModerationReviewPanelProps> = ({
  profiles,
  emptyMessage,
  selectedProfileId,
  onSelectProfile,
  onApprove,
  onReject,
  onMoveToPending,
  onDelete,
  footer,
}) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const selectedProfile = profiles.find((p) => p.id === selectedProfileId) ?? null;

  useEffect(() => {
    setPreviewImage(null);
  }, [selectedProfileId]);

  if (!profiles.length) {
    return (
      <div className="queue-review-empty">
        <div className="empty-state">{emptyMessage}</div>
        {footer}
      </div>
    );
  }

  return (
    <div className="queue-review-layout">
      <aside className="queue-user-list" aria-label="Users in queue">
        <ul className="queue-user-list-items">
          {profiles.map((profile) => {
            const isActive = profile.id === selectedProfileId;
            const thumb = profile.images?.[0];
            return (
              <li key={profile.id}>
                <button
                  type="button"
                  className={`queue-user-item ${isActive ? 'active' : ''}`}
                  onClick={() => onSelectProfile(profile.id)}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <div className="queue-user-item-thumb">
                    {thumb ? (
                      <img src={thumb} alt="" />
                    ) : (
                      <span className="queue-user-item-placeholder">{getProfileListLabel(profile).charAt(0)}</span>
                    )}
                  </div>
                  <div className="queue-user-item-text">
                    <span className="queue-user-item-name">{getProfileListLabel(profile)}</span>
                    <span className="queue-user-item-meta">
                      Score {profile.trust_score} · {new Date(profile.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <span
                    className={`queue-user-item-score ${
                      profile.trust_score >= 70 ? 'score-high' : profile.trust_score >= 30 ? 'score-medium' : 'score-low'
                    }`}
                  >
                    {profile.trust_score}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {footer}
      </aside>

      <section className="queue-profile-detail" aria-label="Profile review">
        {selectedProfile ? (
          <>
            <header className="detail-head">
              <div>
                <p className="detail-kicker">Reviewing</p>
                <h3>{getProfileListLabel(selectedProfile)}</h3>
                <p className="detail-submitted">
                  Submitted {new Date(selectedProfile.created_at).toLocaleString()}
                </p>
              </div>
              <div
                className={`score-chip detail-score ${
                  selectedProfile.trust_score >= 70
                    ? 'score-high'
                    : selectedProfile.trust_score >= 30
                      ? 'score-medium'
                      : 'score-low'
                }`}
              >
                Trust score {selectedProfile.trust_score}
              </div>
            </header>

            <div className="detail-body">
              {selectedProfile.images?.length > 0 && (
                <div className="detail-photos">
                  {selectedProfile.images.map((src, idx) => (
                    <button
                      key={`${selectedProfile.id}-photo-${idx}`}
                      type="button"
                      className="detail-photo-frame"
                      onClick={() => setPreviewImage(src)}
                    >
                      <img src={src} alt={`Photo ${idx + 1}`} />
                    </button>
                  ))}
                </div>
              )}

              <div className="detail-fields">
                <div className="detail-fields-grid">
                  {PROFILE_DETAIL_FIELDS.map(({ key, label }) => (
                    <div key={key} className="detail-field">
                      <span className="detail-field-label">{label}</span>
                      <span className="detail-field-value">
                        {formatProfileFieldDisplay(getProfileFieldValue(selectedProfile, key))}
                      </span>
                    </div>
                  ))}
                  <div className="detail-field">
                    <span className="detail-field-label">Trust score</span>
                    <span className="detail-field-value">{selectedProfile.trust_score}</span>
                  </div>
                  {selectedProfile.text_results && (
                    <div className="detail-field">
                      <span className="detail-field-label">Text analysis score</span>
                      <span className="detail-field-value">{selectedProfile.text_results.score}</span>
                    </div>
                  )}
                </div>

                <div className="detail-field detail-field-block">
                  <span className="detail-field-label">Bio</span>
                  <p className="detail-field-value detail-field-paragraph">
                    {formatProfileFieldDisplay(selectedProfile.bio || '')}
                  </p>
                </div>

                <div className="detail-field detail-field-block">
                  <span className="detail-field-label">Prompts</span>
                  {selectedProfile.prompts?.length ? (
                    <div className="detail-prompts">
                      {selectedProfile.prompts.map((prompt, idx) => (
                        <div key={`${selectedProfile.id}-prompt-${idx}`} className="detail-prompt">
                          <p className="detail-prompt-q">{formatProfileFieldDisplay(prompt.question || '')}</p>
                          <p className="detail-prompt-a">{formatProfileFieldDisplay(prompt.answer || '')}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="detail-field-value detail-field-paragraph">—</p>
                  )}
                </div>

                {!!selectedProfile.image_results?.length && (
                  <div className="detail-field detail-field-block">
                    <span className="detail-field-label">Image checks</span>
                    <ul className="detail-check-list">
                      {selectedProfile.image_results.map((result, idx) => (
                        <li
                          key={`${selectedProfile.id}-check-${idx}`}
                          className={result.passed ? 'result-passed' : 'result-failed'}
                        >
                          Image {idx + 1}: {result.passed ? 'Passed' : 'Failed'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="detail-actions">
              {selectedProfile.status === 'pending' ? (
                <>
                  <button type="button" className="action-button approve-button" onClick={() => onApprove(selectedProfile.id)}>
                    Approve
                  </button>
                  <button type="button" className="action-button reject-button" onClick={() => onReject(selectedProfile.id)}>
                    Reject
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="action-button pending-button"
                  onClick={() => onMoveToPending(selectedProfile.id)}
                >
                  Move to pending
                </button>
              )}
              <button type="button" className="action-button delete-button" onClick={() => onDelete(selectedProfile.id)}>
                Delete
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">Select a user from the list.</div>
        )}
      </section>

      {previewImage && (
        <div className="image-modal-overlay" onClick={() => setPreviewImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="image-modal-close" onClick={() => setPreviewImage(null)} aria-label="Close">
              x
            </button>
            <img src={previewImage} alt="Profile media preview" className="image-modal-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModerationReviewPanel;
